<template>
  <v-card class="rpilot-tsp-preview d-flex flex-column" flat>
    <!-- Apply Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500" :persistent="saving">
      <v-card>
        <v-card-title class="text-h6 px-4 pt-4">
          {{ isCreate ? 'Create Profile' : 'Update Profile' }}
        </v-card-title>

        <v-card-text>
          <p class="text-body-2 mb-4">
            <strong>{{ profileCode }}</strong> | {{ profileTitle }}
          </p>
          <div class="d-flex justify-space-around mb-4">
            <v-chip color="green" label>{{ counts.new }} new</v-chip>
            <v-chip color="orange" label>{{ counts.updated }} updated</v-chip>
            <v-chip color="red" label>{{ counts.deleted }} deleted</v-chip>
          </div>
          <p v-if="subtaskChangeCount > 0" class="text-body-2 text-center mb-4">
            {{ subtaskChangeCount }} subtask change{{ subtaskChangeCount !== 1 ? 's' : '' }}
          </p>

          <v-switch
            v-model="publishOn"
            label="Publish after save"
            color="primary"
            density="compact"
            hide-details
          ></v-switch>
          <v-expand-transition>
            <div v-if="publishOn">
              <v-text-field
                v-model="localPublishMessage"
                label="Publish message"
                variant="outlined"
                density="compact"
                class="mt-2"
              ></v-text-field>
              <p class="text-caption text-medium-emphasis mb-4">
                Publishing makes this revision deployable and read-only, and syncs
                changes into any schedules it is already deployed to.
              </p>
            </div>
          </v-expand-transition>

          <p class="text-body-2">
            {{ isCreate
              ? 'This will create the profile with the structure shown.'
              : 'This will replace the profile\'s structure with the proposal shown.' }}
            Are you sure you want to continue?
          </p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" :disabled="saving" @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="saving" @click="handleConfirm">
            Confirm {{ isCreate ? 'Create' : 'Update' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Detail Dialog -->
    <v-dialog v-model="changesDialog" max-width="700">
      <v-card v-if="selectedItem">
        <v-card-title class="text-h6 px-4 pt-4">
          Changes for: {{ selectedItem.name }}
        </v-card-title>
        <v-card-text>
          <v-list v-if="selectedItem.changeDetails?.length > 0">
            <v-list-subheader>Change Details</v-list-subheader>
            <v-list-item v-for="(detail, index) in selectedItem.changeDetails" :key="index">
              <v-list-item-title>{{ detail }}</v-list-item-title>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 text-medium-emphasis">
            No specific changes to display.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="changesDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Subtasks Dialog -->
    <SubtasksDialog v-model="subtasksDialog" :task="selectedTaskForSubtasks" />

    <!-- Header: profile identity. On create, code/title are double-click
         editable so the user can override what the agent proposed. -->
    <div class="d-flex align-center px-4 pt-3 pb-1">
      <div class="text-subtitle-1 d-flex align-center">
        <v-text-field
          v-if="editingField === 'code'"
          v-model="editValue"
          density="compact"
          variant="outlined"
          hide-details
          autofocus
          style="width: 160px;"
          @keydown.enter.prevent="commitEdit"
          @keydown.esc.prevent="cancelEdit"
          @blur="commitEdit"
        ></v-text-field>
        <strong
          v-else
          :class="{ 'tsp-identity-editable': canEditIdentity }"
          :title="canEditIdentity ? 'Double-click to edit the code' : undefined"
          @dblclick="startEdit('code')"
        >{{ profileCode || '—' }}</strong>

        <span class="text-medium-emphasis mx-1">|</span>

        <v-text-field
          v-if="editingField === 'title'"
          v-model="editValue"
          density="compact"
          variant="outlined"
          hide-details
          autofocus
          style="width: 320px;"
          @keydown.enter.prevent="commitEdit"
          @keydown.esc.prevent="cancelEdit"
          @blur="commitEdit"
        ></v-text-field>
        <span
          v-else
          class="text-medium-emphasis"
          :class="{ 'tsp-identity-editable': canEditIdentity }"
          :title="canEditIdentity ? 'Double-click to edit the title' : undefined"
          @dblclick="startEdit('title')"
        >{{ profileTitle || 'Task set profile' }}</span>
      </div>
      <v-chip v-if="isCreate" size="small" color="green" label class="ml-3">New profile</v-chip>
    </div>

    <!-- Toolbar -->
    <v-toolbar color="primary" density="compact">
      <v-text-field
        v-model="searchQuery"
        label="Search"
        prepend-inner-icon="mdi-magnify"
        clearable
        density="compact"
        hide-details
        class="mx-2"
        style="max-width: 300px;"
      ></v-text-field>

      <v-switch
        v-model="showChangesOnly"
        label="Show Changes Only"
        density="compact"
        hide-details
        class="mx-2"
      ></v-switch>

      <v-spacer></v-spacer>

      <v-btn icon class="mx-1" title="Expand All" @click="expandAll">
        <v-icon>mdi-unfold-more-horizontal</v-icon>
      </v-btn>
      <v-btn icon class="mx-1" title="Collapse All" @click="collapseAll">
        <v-icon>mdi-unfold-less-horizontal</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Bryntum TreeGrid fills the remaining height of the panel -->
    <div class="tsp-grid-wrap flex-grow-1">
      <div ref="gridContainer" class="tsp-tree-grid"></div>
      <div v-if="!previewTree.length" class="tsp-empty-hint text-medium-emphasis">
        No structure yet — describe the profile in the chat and RPilot will
        propose one here for review.
      </div>
    </div>

    <!-- Same action row as the schedules review step: Back leaves the
         workflow, the primary button opens the confirm dialog. Accepting or
         rejecting a pending proposal is done from the page's proposal banner
         (Save… / Reject), exactly as on the Schedules RPilot page. -->
    <v-card-actions>
      <v-btn @click="emit('back')">Back</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :disabled="!hasProposal"
        @click="confirmDialog = true"
      >
        {{ isCreate ? 'Create Profile' : 'Update Profile' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { TreeGrid } from '@bryntum/gantt';
import { CHANGE_TYPES } from '@/utils/TaskSetProfileChangeDetector';
import SubtasksDialog from '@components/dialogs/SubtasksDialog.vue';

import '@bryntum/gantt/gantt.stockholm.css';

const props = defineProps({
  // Rows from buildProposalPreview (baseline = diff of existing vs itself)
  previewTree: { type: Array, default: () => [] },
  // Row id → SubtasksDialog rows (from buildProposalPreview)
  subtaskMap: { type: Map, default: () => new Map() },
  counts: {
    type: Object,
    default: () => ({ new: 0, updated: 0, deleted: 0, subtasksNew: 0, subtasksUpdated: 0, subtasksDeleted: 0 })
  },
  profileTitle: { type: String, default: '' },
  profileCode: { type: String, default: '' },
  isCreate: { type: Boolean, default: false },
  hasProposal: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  // Agent-requested publish state — pre-seeds the dialog's toggle each time it
  // opens; the user can still flip it before applying.
  publishRequested: { type: Boolean, default: false },
  publishMessage: { type: String, default: '' }
});

const emit = defineEmits(['save', 'back', 'update:profileCode', 'update:profileTitle']);

const gridContainer = ref(null);
let treeGrid = null;

const searchQuery = ref('');
const showChangesOnly = ref(false);
const confirmDialog = ref(false);
const publishOn = ref(false);
const localPublishMessage = ref('');
const changesDialog = ref(false);
const subtasksDialog = ref(false);
const selectedItem = ref(null);
const selectedTaskForSubtasks = ref(null);

const subtaskChangeCount = computed(() =>
  (props.counts.subtasksNew || 0) + (props.counts.subtasksUpdated || 0) + (props.counts.subtasksDeleted || 0)
);

// Identity is only editable while creating: there's a proposal to edit, and
// the code/title haven't been persisted yet. Existing profiles keep their
// identity managed through RDrive.
const canEditIdentity = computed(() => props.isCreate && props.hasProposal);
const editingField = ref(null); // 'code' | 'title' | null
const editValue = ref('');

function startEdit(field) {
  if (!canEditIdentity.value) return;
  editingField.value = field;
  editValue.value = field === 'code' ? props.profileCode : props.profileTitle;
}

function commitEdit() {
  const field = editingField.value;
  if (!field) return;
  editingField.value = null;
  const value = editValue.value.trim();
  const current = field === 'code' ? props.profileCode : props.profileTitle;
  if (!value || value === current) return;
  emit(field === 'code' ? 'update:profileCode' : 'update:profileTitle', value);
}

function cancelEdit() {
  editingField.value = null;
}

function initTreeGrid() {
  if (!gridContainer.value) return;

  treeGrid = new TreeGrid({
    appendTo: gridContainer.value,
    columns: [
      { field: 'code', text: 'Code', sortable: false, width: 120 },
      { type: 'tree', field: 'name', text: 'Title', sortable: false, flex: 4 },
      {
        field: 'changeCount',
        text: 'Changes',
        type: 'template',
        sortable: false,
        width: 100,
        align: 'center',
        template: ({ record }) => {
          const color = getChipBackgroundColor(record.changeType);
          const count = record.changeCount || 0;
          return `<div class="gantt-chip" style="background-color: ${color}; cursor: ${count > 0 ? 'pointer' : 'default'};">${count}</div>`;
        }
      },
      {
        field: 'subtaskCount',
        text: 'Subtasks',
        type: 'template',
        sortable: false,
        width: 120,
        align: 'center',
        template: ({ record }) => {
          const count = record.subtaskCount || 0;
          const changes = record.subtaskChanges || 0;
          if (count === 0) return '';
          const hasChanges = changes > 0;
          const chipColor = hasChanges ? '#ff9800' : '#9e9e9e';
          const label = hasChanges
            ? `${count} (${changes} changed)`
            : `${count} subtask${count !== 1 ? 's' : ''}`;
          return `<div class="subtask-chip" style="background-color: ${chipColor}; cursor: pointer;">${label}</div>`;
        }
      }
    ],
    features: {
      cellMenu: {
        items: { removeRow: false }
      }
    },
    store: {
      tree: true,
      data: props.previewTree
    },
    listeners: {
      cellClick: ({ record, column }) => {
        if (column.field === 'changeCount' && record.changeCount > 0) {
          selectedItem.value = {
            name: record.name,
            changeDetails: record.changeDetails || []
          };
          changesDialog.value = true;
        }
        if (column.field === 'subtaskCount' && record.subtaskCount > 0) {
          selectedTaskForSubtasks.value = {
            name: record.name,
            subtaskProfiles: props.subtaskMap.get(record.id) || []
          };
          subtasksDialog.value = true;
        }
      }
    }
  });
}

function getChipBackgroundColor(changeType) {
  switch (changeType) {
    case CHANGE_TYPES.ADDED:
      return '#8bc34a';
    case CHANGE_TYPES.MODIFIED:
      return '#ff9800';
    case CHANGE_TYPES.REMOVED:
      return '#c70000';
    default:
      return '#3ca3f4';
  }
}

function filterTasks() {
  if (!treeGrid) return;

  const query = (searchQuery.value || '').toLowerCase();
  const searchFilter = (record) =>
    !query ||
    record.name?.toLowerCase().includes(query) ||
    record.code?.toLowerCase().includes(query);
  const changesFilter = (record) =>
    !showChangesOnly.value ||
    record.changeType !== CHANGE_TYPES.UNCHANGED ||
    record.subtaskChanges > 0;

  treeGrid.store.filter((record) => searchFilter(record) && changesFilter(record));
}

function expandAll() {
  if (treeGrid) treeGrid.expandAll();
}

function collapseAll() {
  if (treeGrid) treeGrid.collapseAll();
}

// The dialog stays open (Confirm button in its loading state) until the page
// settles the save and calls closeConfirmDialog — mirrors the schedules
// page's Save Tasks dialog.
function handleConfirm() {
  emit('save', {
    publish: publishOn.value,
    publishMessage: localPublishMessage.value
  });
}

// Lets the page's proposal banner drive the same dialog as the action-row
// button, and close it once the save has settled (same contract as
// ReviewStructureStep's openImportDialog / closeImportDialog).
const openConfirmDialog = () => {
  confirmDialog.value = true;
};
const closeConfirmDialog = () => {
  confirmDialog.value = false;
};
defineExpose({ openConfirmDialog, closeConfirmDialog });

watch([searchQuery, showChangesOnly], filterTasks);

// Re-seed from the proposal every time the dialog opens, so a fresh proposal's
// publish request wins over whatever the user chose for the previous one.
watch(confirmDialog, (open) => {
  if (open) {
    publishOn.value = props.publishRequested;
    localPublishMessage.value = props.publishMessage;
  }
});

watch(
  () => props.previewTree,
  (tree) => {
    if (!treeGrid) return;
    treeGrid.store.data = tree;
    filterTasks();
  }
);

onMounted(() => {
  nextTick(initTreeGrid);
});

onUnmounted(() => {
  if (treeGrid) {
    treeGrid.destroy();
    treeGrid = null;
  }
});
</script>

<style lang="scss" scoped>
.rpilot-tsp-preview {
  height: 100%;
  min-height: 0;
  /* App.vue globally sets .v-card { margin: 0 auto }; inside the page's flex
     column that auto margin overrides stretch and collapses the card to
     fit-content width. Force it to fill the main panel. */
  width: 100%;
  margin: 0;
  /* Match the 8px radius of the page panels so the card corners don't peek
     past the shadowed wrapper. */
  border-radius: 8px;
}

.tsp-identity-editable {
  cursor: text;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.3);
}

.tsp-grid-wrap {
  position: relative;
  min-height: 300px;
}

.tsp-tree-grid {
  position: absolute;
  inset: 0;
}

.tsp-empty-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  pointer-events: none;
}

/* Bryntum TreeGrid styling matching ReviewTspChangesStep */
.tsp-tree-grid {
  :deep(.b-icon:not(.b-mask-icon)) {
    color: rgba(0, 0, 0, 0.54);
  }

  :deep(.b-grid-header-container) {
    .b-header {
      box-shadow: none;
    }

    .b-grid-header-scroller.b-header {
      border-bottom: 1px solid #E0E0E0;

      .b-grid-header {
        background-color: #FFFFFF;
      }
    }
  }

  :deep(.b-grid-cell) {
    font-weight: normal;
    color: #000000;
  }

  :deep(.b-tree-parent-row .b-grid-cell) {
    color: #616161;
  }

  :deep(.b-grid-row.b-tree-parent-row.b-tree-expanded) {
    .b-grid-cell:first-child {
      text-overflow: ellipsis;
      display: block;
      line-height: 45px;
    }
  }

  :deep(.b-using-keyboard) {
    .b-grid-cell, .b-grid-header {
      &:focus, &:focus-within {
        outline: 2px solid #0277BD;
      }
    }
  }

  :deep(.gantt-chip) {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1em;
    margin-inline-start: 0.5em;
    padding: 0.3em 1em;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  :deep(.subtask-chip) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 1em;
    padding: 0.3em 0.8em;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }
}
</style>
